import React, { useMemo, useState } from 'react';

import { Autocomplete, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import type { components } from '@luxuryescapes/contract-svc-promo/generated';
import { isValidTreatmentReferenceId } from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import {
  useGetActivePromoDisplayConfig,
  useGetPromoDisplayConfigs,
} from '~/hooks/apis/promo/useGetPromoDisplayConfigs';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useDebounced from '~/hooks/useDebounced';

import {
  type PromoDisplayConfigPickerOption,
  getPromoDisplayConfigPickerOptionLabel,
  getPromoDisplayConfigPickerOptions,
  getPromoDisplayConfigReferenceMessage,
  getPromoDisplayConfigReferenceStatus,
} from './marketingRouterPromoDisplayConfigReference';

const SEARCH_DEBOUNCE_MS = 300;

export type MarketingRouterPromoDisplayConfigFieldProps = {
  /** The stored promo display config id. */
  value: string;
  label: string;
  required: boolean;
  /** The field's description, shown when the id has nothing to report. */
  description?: string;
  onChange: (promoDisplayConfigId: string) => void;
};

/**
 * Picks a promo display config for a treatment by searching the brand's active
 * configs, and checks the stored id the way the router save does.
 *
 * The treatment still stores only the id. Typing searches and is never stored
 * on its own: leaving the field without picking puts the current config back,
 * and emptying the field clears it. A pasted id is taken as soon as it is well
 * formed, so a known id still works without searching.
 */
export default function MarketingRouterPromoDisplayConfigField({
  value,
  label,
  required,
  description,
  onChange,
}: MarketingRouterPromoDisplayConfigFieldProps) {
  const { tenant } = useCurrentTenant();
  const brand = tenant.brand as components['schemas']['Promo Brands'];
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounced(searchText, SEARCH_DEBOUNCE_MS);

  const activeConfigQuery = useGetActivePromoDisplayConfig({ brand, promoDisplayConfigId: value });
  const searchQuery = useGetPromoDisplayConfigs({ brand, search: debouncedSearchText, enabled: isOpen });

  const status = getPromoDisplayConfigReferenceStatus(value, {
    data: activeConfigQuery.data,
    error: activeConfigQuery.error,
  });
  const { error, message } = getPromoDisplayConfigReferenceMessage(status, tenant.title);
  // Memoised because MUI rewrites the input whenever the value changes identity.
  const selected = useMemo<PromoDisplayConfigPickerOption | null>(
    () => (value ? { id: value, config: activeConfigQuery.data ?? undefined } : null),
    [value, activeConfigQuery.data],
  );
  const options = useMemo(
    () => getPromoDisplayConfigPickerOptions(selected, searchQuery.data ?? []),
    [selected, searchQuery.data],
  );
  const searchTerm = debouncedSearchText.trim();

  return (
    <Autocomplete<PromoDisplayConfigPickerOption>
      autoHighlight
      filterSelectedOptions
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      value={selected}
      options={options}
      // svc-promo has already matched the options against the search text.
      filterOptions={(unfiltered) => unfiltered}
      loading={searchQuery.isFetching}
      noOptionsText={
        searchTerm
          ? `No active ${tenant.title} promo display config matches “${searchTerm}”.`
          : `${tenant.title} has no active promo display configs.`
      }
      getOptionLabel={getPromoDisplayConfigPickerOptionLabel}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      onInputChange={(_, text, reason) => {
        // 'reset' is MUI writing the selected config's label back into the
        // input, and 'clear' empties it; neither is a search.
        setSearchText(reason === 'input' ? text : '');
        if (reason === 'input' && isValidTreatmentReferenceId(text.trim())) {
          onChange(text.trim());
        }
      }}
      onChange={(_, next) => onChange(next?.id ?? '')}
      renderOption={(props, option) => {
        const { key: _key, ...optionProps } = props;
        return (
          <Box component="li" key={option.id} {...optionProps}>
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography variant="body2">{getPromoDisplayConfigPickerOptionLabel(option)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {option.config ? `${option.config.header_text} · ${option.id}` : option.id}
              </Typography>
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={message ?? description}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searchQuery.isFetching || status.kind === 'checking' ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ flex: '1 1 320px' }}
    />
  );
}
